import {cloneDeep} from 'lodash';
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {receiveChecklist} from '../redux/checklist/actions';
import {receiveChecklistUserStatus} from '../redux/checklistUserStatus/actions';
import ChecklistService, {ChecklistShape} from '../services/ChecklistService';
import ChecklistUserStatus from '../services/ChecklistUserStatus';
import {EducationPhase} from '../types/enums';
import {Store} from '../types/store';
import {useBoolean} from './useBoolean';

// we only want to load the checklist data one time, so using a global variable to track that
let loaded = false;

export interface ChecklistData {
  loading: boolean;
  checklist: ChecklistShape | null;
}

export function useChecklist(educationPhase: EducationPhase): ChecklistData {
  const loading = useBoolean(false);
  const [checklistWithStatuses, setChecklistWithStatuses] = useState<ChecklistShape | null>(null);
  const dispatch = useDispatch();
  const checklist = useSelector((state: Store) => state.checklist);
  const checklistUserStatus = useSelector((state: Store) => state.checklistUserStatus);

  useEffect(() => {
    if (loaded || loading.value) {
      return;
    }

    loading.setTrue();
    Promise.all([
      ChecklistService.show(educationPhase).then((receivedChecklist) => {
        dispatch(receiveChecklist(receivedChecklist));
      }),
      ChecklistUserStatus.show(educationPhase).then((receivedChecklistUserStatus) => {
        dispatch(receiveChecklistUserStatus(receivedChecklistUserStatus));
      }),
    ])
      .then(() => (loaded = true))
      .finally(loading.setFalse);
  }, [educationPhase]);

  const checklistStringified = JSON.stringify({checklist, checklistUserStatus});
  useEffect(() => {
    if (!checklist || !checklistUserStatus) {
      return;
    }

    const statuses: Record<string, boolean> = {};
    checklistUserStatus?.itemStatuses?.forEach((itemStatus) => {
      statuses[itemStatus.itemId] = itemStatus.status;
    });

    const updatedChecklistWithStatuses = cloneDeep(checklist);
    updatedChecklistWithStatuses?.groups?.forEach((group) => {
      group?.items?.forEach((item) => {
        item.completed = statuses[item._id] || false;
      });
    });
    setChecklistWithStatuses(updatedChecklistWithStatuses);
  }, [checklistStringified]);

  return {
    loading: loading.value,
    checklist: checklistWithStatuses,
  };
}
